import { token } from '@/infrastructure/container';
import { type AppBus, type AppEvents, BUS_SUBSCRIBER_POLICY } from './AppBus';
import { type ITrace } from './Trace';

/**
 * Feeds every bus event onto the trace timeline. Takes the concrete AppBus for handlerCount:
 * when a 'required' event (BUS_SUBSCRIBER_POLICY) reaches nobody but the narrator (count 1), the
 * publish would have been rule 20's silent drop, so bus.zero_handlers makes it visible at
 * runtime. 'quiet-ok' events never alarm; a false alarm on a designed-quiet event is exactly the
 * confident wrong diagnosis the narrator exists to prevent.
 *
 * Resolve it in main.tsx before every store, so each event is narrated before its handlers run
 * and synchronous nested publishes read in causal order. assertBootSubscribers accounts for the
 * narrator's own subscriptions via its narratorHandlers offset.
 */
export type BusSubscriberPolicy = Readonly<Record<keyof AppEvents, 'required' | 'quiet-ok'>>;

export class BusNarrator {
  // `policy` is a parameter rather than a direct module read for one reason: every event in
  // grading-day's map is 'required' today, so the quiet-ok branch would ship untested. A test
  // hands in a policy that marks one event quiet-ok and proves the branch. Production always
  // takes the default.
  constructor(bus: AppBus, trace: ITrace, policy: BusSubscriberPolicy = BUS_SUBSCRIBER_POLICY) {
    for (const event of Object.keys(policy) as (keyof AppEvents)[]) {
      bus.subscribe(event, (data) => {
        trace.event(`bus.${event}`, data);
        if (policy[event] === 'required' && bus.handlerCount(event) === 1) {
          trace.event('bus.zero_handlers', { event });
        }
      });
    }
  }
}

export const TOKEN_BusNarrator = token<BusNarrator>('BusNarrator');
